import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

/**
 * The card: the one surface Palate draws around a group of related content. A section of a
 * settings page, a KPI tile, a list row that opens a record, the frame around a table.
 *
 * DESIGN_SYSTEM.md § Card is the spec, and these classes are the whole of it:
 *
 *   radius   `rounded-lg`        the base `--radius` (10px), the dialog's radius too
 *   border   `border-border`     1px, the border token
 *   surface  `bg-card`           white, `shadow-sm`
 *   padding  one of three (below); nothing else
 *   title    `CardTitle`         16px semibold `text-main`, the same on every card
 *
 * A card never carries its own radius, border colour, background or padding class. If a card
 * needs to differ (a red border on a card that has a problem, a left accent stripe), pass that one
 * class: `cn()` lets it win, and the walk (e2e/design-floors/consistency.ts) still measures the
 * radius, the border width and the padding.
 *
 * A card that opens something is `<Card asChild padding="compact"><Link to="/x">…</Link></Card>`: the
 * link (or button, or label wrapping a radio) is the card, and takes its classes and its marker. Use
 * `cardVariants()` only where even that cannot be (a class string handed to a third-party component).
 */
export const cardVariants = cva(
  "rounded-lg border border-border bg-card text-card-foreground shadow-sm",
  {
    variants: {
      padding: {
        /** The card is a frame: its parts pad themselves (a header band with a rule, a table, a list). */
        none: "",
        /** Dense: a tile, a card in a grid of twelve, a card inside a card. */
        compact: "p-4",
        /** The page's card. */
        default: "p-6",
      },
    },
    defaultVariants: { padding: "default" },
  },
);

/** The tags a card can be: the same box, the right element for what it holds. */
export type CardTag = "div" | "section" | "article" | "aside" | "figure" | "form" | "fieldset" | "ul" | "ol" | "li";

export type CardProps = Omit<React.ComponentProps<"div">, "ref"> &
  Pick<React.ComponentProps<"form">, "noValidate" | "autoComplete" | "action" | "method" | "encType" | "name"> &
  VariantProps<typeof cardVariants> & {
    /** The element to render. `section` for a titled group, `li` for a card in a list, `form` for a form that is the card. */
    as?: CardTag;
    /** The child is the card: a `Link` or a `button` that opens something, drawn as a card. */
    asChild?: boolean;
    /** A ref to the element, whichever tag it is. */
    ref?: React.Ref<HTMLElement>;
  };

function Card({ as: tag = "div", asChild = false, padding, className, ...props }: CardProps) {
  const Component = asChild ? Slot : (tag as React.ElementType);
  // The marker comes last: a card that is also a popover trigger is handed `data-slot="popover-trigger"`
  // by the trigger around it, and the walk finds cards by their marker.
  return <Component className={cn(cardVariants({ padding }), className)} {...props} data-slot="card" />;
}

/** The title row: the title and description on the left, a card-level action on the right. */
function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn("mb-4 flex flex-wrap items-start justify-between gap-3", className)}
      {...props}
    />
  );
}

/**
 * The card's title. `h2` by default (a card on a page is a section of it); `as="h3"` inside a
 * section that already has an h2. The size and weight never change, so every card title reads
 * the same.
 */
function CardTitle({
  as: tag = "h2",
  className,
  ...props
}: React.ComponentProps<"h2"> & { as?: "h2" | "h3" | "h4" }) {
  const Component = tag as React.ElementType;
  return (
    <Component
      data-slot="card-title"
      className={cn("text-base font-semibold leading-6 text-main", className)}
      {...props}
    />
  );
}

/** One line under the title saying what the card is for. */
function CardDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="card-description"
      className={cn("mt-1 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

/** The card's body. No padding of its own: the card pads it. */
function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("space-y-4", className)} {...props} />;
}

/** The card's actions, right-aligned: Cancel, then the one primary. */
function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("mt-6 flex flex-wrap items-center justify-end gap-3", className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
